/**
 * Rules (`/rules`) — de regeltabel met paginering.
 * Endpoint: /api/admin/rules?limit=25&offset=0 (zelfde default als de bundle).
 */
import { useState } from 'react'
import { ErrorNote, Loading, Panel, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { Modal, Field } from '../components/Modal'
import { api } from '../lib/api'
import { num } from '../lib/format'

interface Rule {
  id: number; name: string; category: string | null; scope: string | null
  priority: number; action: string | null; is_active: boolean
}

const SIZES = [25, 50, 100]

export default function Rules() {
  const [size, setSize] = useState(25)
  const [page, setPage] = useState(0)
  const [form, setForm] = useState<Partial<Rule> | null>(null)
  const { run, busy } = useActions()
  const q = useApi<Rule[] | { data: Rule[]; total: number }>(
    `/api/admin/rules?limit=${size}&offset=${page * size}`, [size, page])

  const rows = Array.isArray(q.data) ? q.data : (q.data?.data ?? [])
  const total = Array.isArray(q.data) ? rows.length : (q.data?.total ?? rows.length)

  return (
    <>
      <div className="filters">
        <div className="spacer" style={{ flex: 1 }} />
        <select className="select" value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}>
          {SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button className="btn primary" type="button"
                onClick={() => setForm({ name: '', priority: 100, is_active: true })}>
          + Create
        </button>
      </div>

      <Panel
        title="Rules"
        desc="Automation rules applied by the AI chat engine"
        flush
        right={
          <span className="mut2" style={{ fontSize: 12 }}>
            {total ? page * size + 1 : 0}–{Math.min((page + 1) * size, total)} of {num(total)}
          </span>
        }
      >
        {q.loading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : (
          <>
          <Table cols={[
            { key: 'id', label: 'ID' }, { key: 'n', label: 'Name' },
            { key: 'c', label: 'Category' }, { key: 's', label: 'Scope' },
            { key: 'p', label: 'Priority', num: true }, { key: 'a', label: 'Action' },
            { key: 'ac', label: 'Active' }, { key: 'act', label: '' },
          ]} /* de bundle gebruikt hier een DataGrid met een eigen overlay,
             dus geen lege tabelrij */
            emptyText={null}>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mut2 mono">{r.id}</td>
                <td><b>{r.name}</b></td>
                <td className="mut">{r.category || '—'}</td>
                <td className="mut">{r.scope || '—'}</td>
                <td className="num mono">{num(r.priority)}</td>
                <td className="mut">{r.action || '—'}</td>
                <td>{r.is_active ? <Tag kind="ok">Active</Tag> : <Tag kind="mut">Off</Tag>}</td>
                <td><button className="btn ghost sm" type="button"
                            onClick={() => setForm(r)}>Edit</button></td>
              </tr>
            ))}
          </Table>
          {!rows.length ? <div className="empty">No rows · 0–0 of 0</div> : null}
          </>
        )}
      </Panel>
      {form ? (
        <Modal
          title={form.id ? `Edit rule — ${form.name}` : 'Create rule'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setForm(null)}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                type="button"
                disabled={!form.name || busy === 'Save rule'}
                onClick={() => run({
                  label: 'Save rule',
                  request: () => form.id
                    ? api.patch(`/api/admin/rules/${form.id}`, form)
                    : api.post('/api/admin/rules', form),
                  onDone: () => { setForm(null); q.refetch() },
                  successText: 'Rule saved.',
                })}
              >
                Save
              </button>
            </>
          }
        >
          <Field label="Name">
            <input className="inp" value={form.name ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, name: e.target.value }))} />
          </Field>
          <Field label="Categorie">
            <input className="inp" value={form.category ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, category: e.target.value }))} />
          </Field>
          <Field label="Scope">
            <input className="inp" value={form.scope ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, scope: e.target.value }))} />
          </Field>
          <Field label="Prioriteit">
            <input className="inp" type="number" value={form.priority ?? 100}
                   onChange={(e) => setForm((f) => ({ ...f!, priority: Number(e.target.value) }))} />
          </Field>
          <Field label="Actie">
            <textarea className="inp" value={form.action ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f!, action: e.target.value }))} />
          </Field>
          <label className="chkrow">
            <span className={form.is_active ? 'chk on' : 'chk'}
                  onClick={() => setForm((f) => ({ ...f!, is_active: !f!.is_active }))}>
              {form.is_active ? '✓' : ''}
            </span>
            Active
          </label>
        </Modal>
      ) : null}
    </>
  )
}
